"use client";

import React from "react";
import Input, { Textarea } from "@/components/ui/input/input";
import { useFormContext } from "react-hook-form";
import ImageUpload from "@/components/ui/input/fileUpload";
import {
  IconHelp,
  IconBrandTelegram,
  IconBrandTwitterFilled,
  IconBrandDiscordFilled,
  IconWorld,
} from "@tabler/icons-react";
import { TokenFormData } from "@/lib/builderFormSchema";
import { Icons } from "@/components/Icons";
import Tooltip from "@/components/ui/tooltip";

const StepOne = () => {
  const {
    register,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useFormContext<TokenFormData>();

  const currentImage = watch("image");
  const previewUrl = watch("previewUrl");

  // Update preview URL when image changes
  React.useEffect(() => {
    if (currentImage && currentImage.length > 0) {
      const file = currentImage[0];
      if (file && file instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setValue("previewUrl", reader.result as string, {
            shouldValidate: false,
          });
        };
        reader.readAsDataURL(file);
      }
    } else if (!currentImage || currentImage.length === 0) {
      setValue("previewUrl", "", { shouldValidate: false });
    }
  }, [currentImage, setValue]);

  return (
    <div className="space-y-6">
      <h2 className="text-white text-xl font-semibold mb-2">
        1- Set Up Your Token Information
      </h2>
      <p className="text-muted mb-6 text-offwhite">
        Define the basic details of your token. These are the foundational
        elements that will represent your project on the blockchain.
      </p>

      {/* IMAGE UPLOAD */}
      <ImageUpload
        label="Upload Token Image"
        value={watch("image")}
        error={errors.image?.message}
        onChange={(files) =>
          setValue("image", files as FileList, { shouldValidate: true })
        }
        previewUrl={previewUrl}
        setPreviewUrl={(url) =>
          setValue("previewUrl", url || "", { shouldValidate: false })
        }
      />

      <Input
        label="Token Name"
        placeholder="Enter your token's name (e.g., MyToken)"
        {...register("tokenName")}
        error={errors.tokenName?.message}
        endIcon={
          <Tooltip content="Choose a unique and memorable name for your token. This will be displayed across all platforms and exchanges.">
            <IconHelp size={16} stroke={2} />
          </Tooltip>
        }
      />

      <Textarea
        label="Token Description"
        placeholder="Describe your token's purpose and use case"
        rows={6}
        {...register("tokenDescription")}
        error={errors.tokenDescription?.message}
      />

      <Input
        label="Token Symbol"
        placeholder="Enter the token symbol (e.g., MT)"
        {...register("tokenSymbol")}
        error={errors.tokenSymbol?.message}
        endIcon={
          <Tooltip content="A short abbreviation for your token (3-5 characters). This will be used for trading pairs and ticker symbols.">
            <IconHelp size={16} stroke={2} />
          </Tooltip>
        }
      />

      <div>
        <label className="text-sm text-muted font-medium mb-1">
          Token Supply
        </label>
        <Input
          disabled
          value="1,000,000,000"
          className="bg-[#1C1C23]  border border-[#282833]"
          innerClassName="text-offwhite "
          endIcon={<span className="text-base text-offwhite">Token</span>}
        />
        <p className="text-muted text-[#EBEBEF] text-sm mt-1">
          The contract has a fixed total supply of 1 billion tokens
        </p>
      </div>

      <div>
        <label className="text-sm text-muted font-medium mb-1">Decimals</label>
        <Input
          disabled
          value="6"
          innerClassName="text-offwhite"
          className="bg-[#1C1C23] border border-[#282833]"
        />
        <p className="text-muted text-[#EBEBEF] text-sm mt-1">
          The contract has a fixed decimal setting of 6
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          placeholder="Enter Telegram group link (optional)"
          icon={<Icons.Telegram />}
          {...register("telegram")}
          error={errors.telegram?.message}
        />
        <Input
          placeholder="Enter Twitter handle (optional)"
          icon={<Icons.Twitter />}
          {...register("twitter")}
          error={errors.twitter?.message}
        />
        <Input
          placeholder="Enter Discord invite link (optional)"
          icon={<Icons.Discord />}
          {...register("discord")}
          error={errors.discord?.message}
        />
        <Input
          placeholder="Enter your website link (optional)"
          icon={<Icons.Website />}
          {...register("website")}
          error={errors.website?.message}
        />
      </div>
    </div>
  );
};

export default StepOne;
